import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

// Detect platform
const isNativePlatform = Capacitor.isNativePlatform();

// Storage keys with secure prefix
const SECURE_PREFIX = 'secure_';
const ACCESS_TOKEN_KEY = `${SECURE_PREFIX}access_token`;
const REFRESH_TOKEN_KEY = `${SECURE_PREFIX}refresh_token`;

export const setAccessToken = async (token) => {
  if (!token) return;
  
  if (isNativePlatform) {
    // Native: Use Preferences API (backed by Keychain/Keystore)
    await Preferences.set({ key: ACCESS_TOKEN_KEY, value: token });
  } else {
    // Web: Use sessionStorage (cleared on tab close, more secure than localStorage)
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  }
};

export const setRefreshToken = async (token) => {
  if (!token) return;
  
  if (isNativePlatform) {
    // Native: Use Preferences API (backed by Keychain/Keystore)
    await Preferences.set({ key: REFRESH_TOKEN_KEY, value: token });
  } else {
    // Web: DO NOT store refresh token
    // It should remain in HTTP-only cookie managed by the backend
    console.warn('Web platform: Refresh token managed via HTTP-only cookies, not stored in frontend');
  }
};

export const setTokens = async (accessToken, refreshToken) => {
  await setAccessToken(accessToken);
  if (refreshToken) {
    await setRefreshToken(refreshToken);
  }
};

export const getAccessToken = async () => {
  if (isNativePlatform) {
    const { value } = await Preferences.get({ key: ACCESS_TOKEN_KEY });

    return value;
  } else {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }
};

export const getAccessTokenSync = () => {
  if (!isNativePlatform) {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }
  console.warn('Sync token access not supported on native platforms - use async getAccessToken()');

  return null;
};


export const getRefreshToken = async () => {
  if (isNativePlatform) {
    const { value } = await Preferences.get({ key: REFRESH_TOKEN_KEY });

    return value;
  } else {
    // Web: Refresh token is in HTTP-only cookie, not accessible from JavaScript
    return null;
  }
};

export const clearTokens = async () => {
  if (isNativePlatform) {
    await Preferences.remove({ key: ACCESS_TOKEN_KEY });
    await Preferences.remove({ key: REFRESH_TOKEN_KEY });
  } else {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    // Refresh token cookie will be cleared by backend on logout
  }
};


export const hasTokens = async () => {
  const accessToken = await getAccessToken();

  return !!accessToken;
};

const secureTokenStorage = {
  setAccessToken,
  setRefreshToken,
  setTokens,
  getAccessToken,
  getAccessTokenSync,
  getRefreshToken,
  clearTokens,
  hasTokens,
  isNativePlatform
};

export default secureTokenStorage;
